import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Snowflake } from 'lucide-react';
import { useSale, saleAppliesTo, salePercent, salePrice, fmtPrice } from '../utils/sale';
import { getProducts } from '../api/api';
import { productImgUrl } from '../utils/imgUrl';
import { SaleCountdown } from './SaleCountdown';

const FEATURED_CATEGORY = 'frozin-400-products';

const SaleSpotlight = () => {
  const sale = useSale();
  const [products, setProducts] = useState([]);
  const featured = { category: FEATURED_CATEGORY };
  const active = saleAppliesTo(sale, featured);
  const percent = salePercent(sale, featured);

  useEffect(() => {
    if (!active) return;
    getProducts({ category: FEATURED_CATEGORY })
      .then(setProducts)
      .catch(() => setProducts([]));
  }, [active]);

  if (!active || products.length === 0) return null;

  return (
    <section className="relative py-14 sm:py-20 overflow-hidden bg-gradient-to-br from-cyan-950 via-blue-950 to-slate-900 text-white" data-testid="sale-spotlight">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-400/30 backdrop-blur-sm mb-5">
              <Snowflake size={16} className="text-cyan-300" />
              <span className="text-xs font-semibold text-cyan-300 uppercase tracking-[0.2em]">Limited Time Offer</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Cold Plunge Sale —{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400">
                {percent}% OFF
              </span>
            </h2>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 max-w-xl">
              Recover like a pro. Every Frozin cold plunge is {percent}% off — but only while the sale lasts.
            </p>
            <SaleCountdown className="mb-6 bg-white/90" item={featured} />
            <div>
              <Link
                to="/products?category=frozin-400-products"
                data-testid="sale-spotlight-cta"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-cyan-500/30 transform hover:scale-105 transition-all duration-300"
              >
                <span>Shop Cold Plunges</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            {products.slice(0, 2).map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.category}/${p.slug || p.id}`}
                data-testid={`sale-spotlight-product-${p.slug}`}
                className="group relative bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
              >
                <span className="absolute top-3 left-3 z-10 bg-cyan-500 text-white text-xs font-bold rounded-full px-2.5 py-1 shadow">
                  {percent}% OFF
                </span>
                <div className="aspect-[4/3] bg-white">
                  <img
                    src={productImgUrl(p, p.image, 600)}
                    alt={p.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2 line-clamp-1">{p.name}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl sm:text-2xl font-bold text-cyan-300">
                      {fmtPrice(salePrice(p.price, sale, p))}
                    </span>
                    <span className="text-sm text-gray-400 line-through">{fmtPrice(p.price)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SaleSpotlight;
